import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const inputStyle = {
  width: '100%',
  height: 44,
  padding: '0 12px',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  fontSize: 14,
  color: '#111827',
  backgroundColor: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const selectStyle = { ...inputStyle };

export default function MarketRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    firmName: '',
    title: '',
    phone: '',
    investorType: 'fund',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.password || !form.firmName.trim()) {
      setError('Please complete all required fields.');
      return;
    }
    if (form.password.length < 8 || !/\d/.test(form.password) || !/[A-Z]/.test(form.password)) {
      setError('Password must be at least 8 characters and include a number and an uppercase letter.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match. Please re-enter your password.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/market/auth/register`, form);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', padding: 24 }}>
        <div style={{ maxWidth: 480, backgroundColor: '#fff', borderRadius: 16, padding: 32, textAlign: 'center', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', marginBottom: 12 }}>Application received</div>
          <p style={{ color: '#4b5563', fontSize: 15, lineHeight: 1.7, marginBottom: 20 }}>
            Thank you for applying. Our team will review your application and
            contact you within 1–2 business days. Check your email for a
            confirmation message.
          </p>
          <button
            type="button"
            onClick={() => navigate('/market/login')}
            style={{
              backgroundColor: '#3a6ad9',
              color: '#fff',
              border: 'none',
              borderRadius: 100,
              padding: '12px 28px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '40px 16px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Link to="/market" style={{ textDecoration: 'none', display: 'inline-block' }}>
            <img
              src="/doorly-market-logo.png"
              alt="Doorly Market"
              style={{ height: '36px', width: 'auto', display: 'block', margin: '0 auto' }}
            />
          </Link>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#111827', margin: '16px 0 4px' }}>
            Request Investor Access
          </h1>
          <p style={{ color: '#6b7280', fontSize: 14 }}>
            Tell us about you and your firm. Reviewed within 1–2 business days.
          </p>
        </div>

        <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 28 }}>
          {error && (
            <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 10, backgroundColor: '#fff5f6', border: '1px solid #ffd6db', color: '#ff082f', fontSize: 13 }}>
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} noValidate>
            <Section label="Your details">
              <Row>
                <Field label="First name" name="firstName" value={form.firstName} onChange={handleChange} required />
                <Field label="Last name" name="lastName" value={form.lastName} onChange={handleChange} required />
              </Row>
              <Row>
                <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
                <Field label="Phone" name="phone" value={form.phone} onChange={handleChange} />
              </Row>
              <Row>
                <Field
                  label="Password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  hint="8+ chars, 1 number, 1 uppercase"
                />
                <Field
                  label="Confirm password"
                  name="confirmPassword"
                  type="password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  hint="Must match password"
                />
              </Row>
            </Section>

            <Section label="Firm">
              <Row>
                <Field label="Firm name" name="firmName" value={form.firmName} onChange={handleChange} required />
                <Field as="select" label="Investor type" name="investorType" value={form.investorType} onChange={handleChange}>
                  <option value="fund">Credit fund</option>
                  <option value="family_office">Family office</option>
                  <option value="reit">REIT</option>
                  <option value="trade_desk">Trade Desk</option>
                  <option value="individual">Individual</option>
                  <option value="other">Other</option>
                </Field>
              </Row>
              <Row>
                <Field label="Title" name="title" value={form.title} onChange={handleChange} placeholder="Portfolio Manager" />
                <span />
              </Row>
            </Section>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                height: 48,
                borderRadius: 100,
                border: 'none',
                backgroundColor: '#3a6ad9',
                color: '#fff',
                fontWeight: 700,
                fontSize: 15,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </div>

        <div style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: '#6b7280' }}>
          Already registered? <Link to="/market/login" style={{ color: '#3a6ad9', fontWeight: 600 }}>Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>{children}</div>;
}

function Field({ label, name, value, onChange, type = 'text', required, placeholder, hint, as, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
        {label}{required ? ' *' : ''}
      </label>
      {as === 'select' ? (
        <select name={name} value={value} onChange={onChange} style={selectStyle}>{children}</select>
      ) : (
        <input
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          style={inputStyle}
        />
      )}
      {hint && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
